import type { ReactNode } from 'react';
import type * as React from 'react';

import { cn } from '@/lib/utils';

export interface PendingRegionProps extends React.ComponentProps<'div'> {
  /**
   * What is loading — "Loading limits", not "Loading" (spec 079 FR-022).
   *
   * Two regions on one screen both announcing "Loading" tell a reader using a screen
   * reader that something is happening twice, and nothing about which half is which.
   */
  label: string;
  children: ReactNode;
  className?: string;
}

/**
 * The announcement wrapper every placeholder sits inside (spec 079 FR-021).
 *
 * This carries the accessibility contract that `LoadingState` used to hold — status role,
 * polite live region, busy state, and an `sr-only` label — lifted out *before* that
 * component was deleted rather than after, so the conversion could not quietly drop it.
 * That failure mode is the specific reason this component exists as its own thing: a
 * prettier skeleton that stops announcing itself is a visual upgrade and an
 * accessibility regression, and nothing about the diff would look wrong.
 *
 * The blocks inside are `aria-hidden` (see `Skeleton`), so this label is the whole of
 * what assistive technology is told: one region, one sentence, naming its subject.
 */
export function PendingRegion({
  label,
  children,
  className,
  ...props
}: PendingRegionProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn('flex min-h-0 flex-1 flex-col', className)}
      {...props}
    >
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}
